/**
 * Settings panel — Phase 3 overlay (DOM).
 * Shown when state.scene === "settings".
 * Replaces renderSettingsScene() in canvas-render-adapter.
 */

import type { GameStateView } from "../../application/ports.js";
import { t } from "../../i18n/index.js";

const HP_OPTIONS = ["1×", "1.2×", "1.5×", "2×", "3×", "10×"];
const WAVES_OPTIONS = ["+0", "+5", "+10", "+15", "+20"];

export interface SettingsPanelCallbacks {
  onHpChange?: (index: number) => void;
  onWavesChange?: (index: number) => void;
  onToggleChange?: (key: "extraBanishCost" | "healOnReturn") => void;
  onStart?: () => void;
  onCancel?: () => void;
}

export interface SettingsPanel {
  update(state: GameStateView): void;
  destroy(): void;
}

export function createSettingsPanel(
  container: HTMLElement,
  cbs: SettingsPanelCallbacks
): SettingsPanel {
  const el = document.createElement("div");
  el.className = "hud-settings-panel";
  el.style.display = "none";
  container.appendChild(el);

  // ── Build DOM structure once ──────────────────────────────────

  // Title
  const titleEl = document.createElement("div");
  titleEl.className = "hud-settings__title";
  el.appendChild(titleEl);

  // Panel content wrapper
  const panel = document.createElement("div");
  panel.className = "hud-settings__panel hud-panel";
  el.appendChild(panel);

  // Section: HP
  const hpSection = document.createElement("div");
  hpSection.className = "hud-settings__section";
  const hpLabel = document.createElement("div");
  hpLabel.className = "hud-settings__section-label";
  hpSection.appendChild(hpLabel);
  const hpGrid = document.createElement("div");
  hpGrid.className = "hud-settings__option-grid";
  HP_OPTIONS.forEach((optLabel, i) => {
    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = "hud-settings__option-btn";
    btn.textContent = optLabel;
    btn.dataset.index = String(i);
    btn.dataset.kind = "hp";
    btn.addEventListener("click", () => cbs.onHpChange?.(i));
    hpGrid.appendChild(btn);
  });
  hpSection.appendChild(hpGrid);
  panel.appendChild(hpSection);

  // Section: Waves
  const wavesSection = document.createElement("div");
  wavesSection.className = "hud-settings__section";
  const wavesLabel = document.createElement("div");
  wavesLabel.className = "hud-settings__section-label";
  wavesSection.appendChild(wavesLabel);
  const wavesGrid = document.createElement("div");
  wavesGrid.className = "hud-settings__option-grid";
  WAVES_OPTIONS.forEach((optLabel, i) => {
    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = "hud-settings__option-btn";
    btn.textContent = optLabel;
    btn.dataset.index = String(i);
    btn.dataset.kind = "waves";
    btn.addEventListener("click", () => cbs.onWavesChange?.(i));
    wavesGrid.appendChild(btn);
  });
  wavesSection.appendChild(wavesGrid);
  panel.appendChild(wavesSection);

  // Section: Toggles
  const toggleSection = document.createElement("div");
  toggleSection.className = "hud-settings__section";
  const toggleLabel = document.createElement("div");
  toggleLabel.className = "hud-settings__section-label";
  toggleSection.appendChild(toggleLabel);

  const togglesContainer = document.createElement("div");
  togglesContainer.className = "hud-settings__toggles";

  // extraBanishCost toggle
  const ebRow = document.createElement("label");
  ebRow.className = "hud-settings__toggle-row";
  const ebCheck = document.createElement("input");
  ebCheck.type = "checkbox";
  ebCheck.className = "hud-settings__toggle-check";
  ebCheck.addEventListener("change", () => cbs.onToggleChange?.("extraBanishCost"));
  const ebText = document.createElement("span");
  ebText.className = "hud-settings__toggle-text";
  ebRow.appendChild(ebCheck);
  ebRow.appendChild(ebText);
  togglesContainer.appendChild(ebRow);

  // healOnReturn toggle
  const hrRow = document.createElement("label");
  hrRow.className = "hud-settings__toggle-row";
  const hrCheck = document.createElement("input");
  hrCheck.type = "checkbox";
  hrCheck.className = "hud-settings__toggle-check";
  hrCheck.addEventListener("change", () => cbs.onToggleChange?.("healOnReturn"));
  const hrText = document.createElement("span");
  hrText.className = "hud-settings__toggle-text";
  hrRow.appendChild(hrCheck);
  hrRow.appendChild(hrText);
  togglesContainer.appendChild(hrRow);

  toggleSection.appendChild(togglesContainer);
  panel.appendChild(toggleSection);

  // XP multiplier display
  const xpMultEl = document.createElement("div");
  xpMultEl.className = "hud-settings__xp-mult";
  panel.appendChild(xpMultEl);

  // Action buttons
  const actionsRow = document.createElement("div");
  actionsRow.className = "hud-settings__actions";

  const startBtn = document.createElement("button");
  startBtn.type = "button";
  startBtn.className = "hud-settings__btn hud-settings__btn--start";
  startBtn.addEventListener("click", () => cbs.onStart?.());

  const cancelBtn = document.createElement("button");
  cancelBtn.type = "button";
  cancelBtn.className = "hud-settings__btn hud-settings__btn--cancel";
  cancelBtn.addEventListener("click", () => cbs.onCancel?.());

  actionsRow.appendChild(startBtn);
  actionsRow.appendChild(cancelBtn);
  panel.appendChild(actionsRow);

  // ── Update logic ──────────────────────────────────────────────

  function update(state: GameStateView): void {
    const visible = state.scene === "settings";
    el.style.display = visible ? "" : "none";
    if (!visible) return;

    const draft = state.settings?.draft;
    const fieldId = state.settings?.fieldId ?? "";

    titleEl.textContent = t("settings.title", { field: fieldId });
    hpLabel.textContent = t("settings.hordeHp");
    wavesLabel.textContent = t("settings.extraHordes");
    toggleLabel.textContent = t("settings.trials");
    ebText.textContent = t("settings.resilientHorrors");
    hrText.textContent = t("settings.unholyVigor");
    startBtn.textContent = t("settings.begin");
    cancelBtn.textContent = t("settings.retreat");

    // Update HP option buttons
    const hpBtns = hpGrid.querySelectorAll<HTMLButtonElement>(".hud-settings__option-btn");
    hpBtns.forEach((btn, i) => {
      const isActive = draft?.hpMultiplierOption === i;
      btn.classList.toggle("hud-settings__option-btn--active", isActive);
    });

    // Update Waves option buttons
    const wavesBtns = wavesGrid.querySelectorAll<HTMLButtonElement>(".hud-settings__option-btn");
    wavesBtns.forEach((btn, i) => {
      const isActive = draft?.wavesOption === i;
      btn.classList.toggle("hud-settings__option-btn--active", isActive);
    });

    // Update toggles (prevent firing change event by setting checked directly)
    const ebChecked = Boolean(draft?.extraBanishCost);
    if (ebCheck.checked !== ebChecked) ebCheck.checked = ebChecked;
    const hrChecked = Boolean(draft?.healOnReturn);
    if (hrCheck.checked !== hrChecked) hrCheck.checked = hrChecked;

    // XP multiplier (pre-computed by application layer, no domain import needed)
    const xpMult = state.settings?.xpMultiplier ?? 1;
    xpMultEl.textContent = t("settings.graceMult", { mult: String(xpMult) });
  }

  function destroy(): void {
    el.remove();
  }

  return { update, destroy };
}
